// Module 12 — Bottom navigation mobile selon rôle + sync online + offline UI
//
// Auto-injecte une bottom-nav sur mobile (≤768px) selon le rôle de l'utilisateur.
// Met à jour l'état actif au changement de hash. Désactivé en mode standalone si
// la window est un wizard fullscreen.

const MOBILE_QUERY = '(max-width: 768px)';
const NAV_ID = 'm12-mobile-bottom-nav';
const TOAST_ID = 'm12-offline-toast';

// Note : pas d'onglet "Messages" — la messagerie (tech-messages) est contextuelle par
// intervention (GET /tech-messages exige interventionId), il n'existe pas de boîte de
// réception globale. Un onglet #/messages pointait vers une route inexistante (corrigé
// 2026-07-13, cf. AUDIT_TECHNICIEN_MOBILE.md §2.1) — retiré plutôt que de créer une page
// pour une fonctionnalité qui n'existe pas encore sous cette forme.
const TECH_NAV = [
  { hash: '#/planning', icon: '📅', label: 'Planning' },
  { hash: '#/interventions', icon: '🔨', label: 'Interv.' },
  { hash: '#/ma-paye', icon: '💰', label: 'Paye' },
  { hash: '#/mes-absences', icon: '🏖', label: 'Congés' },
];
const ADMIN_NAV = [
  { hash: '#/dashboard', icon: '📊', label: 'Cockpit' },
  { hash: '#/clients', icon: '👥', label: 'Clients' },
  { hash: '#/equipe', icon: '👷', label: 'Équipe' },
  { hash: '#/finance', icon: '💰', label: 'Finance' },
  { icon: '☰', label: 'Menu', action: 'toggle-sidebar' },
];
const VENDEUR_NAV = [
  { hash: '#/clients', icon: '👥', label: 'Clients' },
  { hash: '#/installations', icon: '🏗', label: 'Installations' },
  { hash: '#/ma-paye', icon: '💰', label: 'Commissions' },
];

function getCurrentUser() {
  // 'user' est la clé réellement écrite par api-client.js au login (cf. AUDIT_TECHNICIEN_MOBILE.md
  // §2.3 : 'current_user' n'est jamais écrite nulle part, ce repli ne servait qu'à cacher le vrai
  // problème derrière le fallback JWT ci-dessous).
  try {
    const raw = localStorage.getItem('user');
    if (raw) return JSON.parse(raw);
  } catch {}
  // Fallback : décoder le JWT (si 'user' absent/corrompu)
  try {
    const tok = localStorage.getItem('auth_token');
    if (!tok) return null;
    const payload = JSON.parse(atob(tok.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return { role: payload.role, email: payload.email, id: payload.sub };
  } catch {}
  return null;
}

function getNavItemsForRole(role) {
  if (role === 'TECHNICIAN') return TECH_NAV;
  if (role === 'VENDEUR') return VENDEUR_NAV;
  if (role === 'ADMIN' || role === 'MANAGER' || role === 'COMMERCIAL') return ADMIN_NAV;
  return null;
}

function isMobile() {
  return window.matchMedia(MOBILE_QUERY).matches;
}

function isOnAuthPage() {
  const h = location.hash || '';
  return h === '' || h === '#/' || h.startsWith('#/login') || h.startsWith('#/connexion');
}

export function renderBottomNav() {
  const old = document.getElementById(NAV_ID);
  if (old) old.remove();
  document.body.classList.remove('has-mobile-bottom-nav');

  if (!isMobile()) return null;
  if (isOnAuthPage()) return null;

  const user = getCurrentUser();
  if (!user) return null;
  const items = getNavItemsForRole(user.role);
  if (!items) return null;

  const currentHash = location.hash || '#/';
  const nav = document.createElement('nav');
  nav.id = NAV_ID;
  nav.className = 'mobile-bottom-nav mobile-only';
  nav.setAttribute('aria-label', 'Navigation principale mobile');
  nav.innerHTML = items.map(it => {
    if (it.action === 'toggle-sidebar') {
      return `<a href="javascript:void(0)" onclick="window.__toggleMobileSidebar && window.__toggleMobileSidebar()" data-action="toggle-sidebar">
        <span class="nav-icon" aria-hidden="true">${it.icon}</span>
        <span>${it.label}</span>
      </a>`;
    }
    const active = currentHash === it.hash || currentHash.startsWith(it.hash + '/');
    return `<a href="${it.hash}" class="${active ? 'active' : ''}" data-route="${it.hash}">
      <span class="nav-icon" aria-hidden="true">${it.icon}</span>
      <span>${it.label}</span>
    </a>`;
  }).join('');
  document.body.appendChild(nav);
  document.body.classList.add('has-mobile-bottom-nav');
  return nav;
}

function updateActive() {
  const nav = document.getElementById(NAV_ID);
  if (!nav) return;
  const currentHash = location.hash || '#/';
  nav.querySelectorAll('a').forEach(a => {
    const route = a.dataset.route;
    a.classList.toggle('active', currentHash === route || currentHash.startsWith(route + '/'));
  });
}

// ============================ OFFLINE TOAST ============================
function showOfflineToast(text, durationMs = 3500) {
  const old = document.getElementById(TOAST_ID);
  if (old) old.remove();
  const t = document.createElement('div');
  t.id = TOAST_ID;
  t.className = 'offline-toast';
  t.textContent = text;
  document.body.appendChild(t);
  setTimeout(() => { try { t.remove(); } catch {} }, durationMs);
}

// ============================ SYNC OFFLINE QUEUE ============================
async function syncOfflineQueue() {
  let queue = [];
  try {
    queue = JSON.parse(localStorage.getItem('m12_offline_queue') || '[]');
  } catch {}
  if (!queue.length) return { ok: true, synced: 0 };
  const remaining = [];
  let synced = 0;
  for (const req of queue) {
    try {
      const r = await fetch(req.url, {
        method: req.method,
        headers: req.headers || { 'Content-Type': 'application/json' },
        body: req.body || undefined,
      });
      if (r.ok || r.status < 500) synced++;
      else remaining.push(req);
    } catch (err) {
      remaining.push(req);
    }
  }
  localStorage.setItem('m12_offline_queue', JSON.stringify(remaining));
  return { ok: true, synced, remaining: remaining.length };
}

// Écoute les messages du Service Worker pour queue les requêtes offline
function listenServiceWorkerMessages() {
  if (!navigator.serviceWorker) return;
  navigator.serviceWorker.addEventListener('message', (ev) => {
    if (!ev.data || ev.data.type !== 'QUEUE_OFFLINE_REQUEST') return;
    let queue = [];
    try { queue = JSON.parse(localStorage.getItem('m12_offline_queue') || '[]'); } catch {}
    queue.push({
      url: ev.data.url,
      method: ev.data.method,
      body: ev.data.body,
      timestamp: ev.data.timestamp,
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (localStorage.getItem('auth_token') || '') },
    });
    localStorage.setItem('m12_offline_queue', JSON.stringify(queue));
    showOfflineToast(`📥 Action sauvegardée hors ligne (${queue.length} en attente)`);
  });
}

// ============================ AUTO-BOOT ============================
function bootMobileNav() {
  renderBottomNav();
  // Hash change → mettre à jour l'actif
  window.addEventListener('hashchange', () => {
    if (!document.getElementById(NAV_ID)) renderBottomNav();
    else updateActive();
  });
  // Resize → afficher / cacher
  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(renderBottomNav, 200);
  });
  // Online/Offline
  window.addEventListener('online', async () => {
    showOfflineToast('✅ De retour en ligne. Synchronisation...', 3000);
    const r = await syncOfflineQueue();
    if (r.synced > 0) showOfflineToast(`✅ ${r.synced} action(s) synchronisée(s)`, 3500);
  });
  window.addEventListener('offline', () => {
    showOfflineToast('📡 Hors ligne — tes actions seront sauvegardées', 4000);
  });
  listenServiceWorkerMessages();
}

if (typeof window !== 'undefined') {
  window.M12Nav = { renderBottomNav, syncOfflineQueue, showOfflineToast };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootMobileNav);
  } else {
    bootMobileNav();
  }
}
